import {ApolloError} from '@sail/data';
import {useSelector} from 'src/v2/state/store';
import {SupportedCurrency} from 'src/v2/state/types';
import useWalletBalancesQuery from 'src/api/graphql/useWalletBalancesQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';

export type CurrencyBalance = {
  currency: SupportedCurrency;
} & (
  | {
      amountInUsdMinor: number | null;
      decimals: number;
    }
  | {
      amountInUsdMinor: null;
      decimals: null;
    }
);

export const useCurrencyBalances = (): {
  data: CurrencyBalance[];
  loading: boolean;
  error: ApolloError | undefined;
} => {
  const activeWallet = useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const sessionId = useSelector((state) => state.id);
  const clientSecret = useSelector((state) => state.clientSecret);
  const {mode} = useSessionContext();

  const {
    data: queryData,
    loading,
    error,
  } = useWalletBalancesQuery(
    activeWallet
      ? {
          variables: {
            input: {
              ...(mode === 'pay'
                ? {crypto_payin_session: sessionId}
                : {crypto_setup_session: sessionId}),
              network: activeWallet.network,
              wallet_address: activeWallet.address,
              client_secret: clientSecret,
            },
          },
          component: 'useCurrencyBalances',
        }
      : {skip: true, component: 'useCurrencyBalances'},
  );
  const balances = queryData?.v1CryptoInternalWalletBalances?.balances;

  const supportedCurrencies = useSelector((state) =>
    state.supportedCurrencies.filter(
      (currency) => currency.currencyNetwork === activeWallet?.network,
    ),
  );

  const data = supportedCurrencies.map((currency) => {
    // If there's an error, don't show the balance. Rely on wallet to prompt user about insufficient balance.
    if (loading || error) {
      return {
        currency,
        amountInUsdMinor: null,
        decimals: null,
      };
    }

    // Find matching token balance based on contract address
    const matchingBalance = balances?.find(
      (balance) =>
        balance.tokenAddress.toLowerCase() ===
        currency.contractAddress.toLowerCase(),
    );

    return {
      currency,
      amountInUsdMinor: +(matchingBalance?.balance ?? '0'),
      decimals: currency.currencyMinorUnits,
    };
  });

  return {data, loading, error};
};
